import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { 
    FileIcon, 
    ExternalLink, 
    FolderIcon, 
    FileTextIcon, 
    FileCodeIcon, 
    ImageIcon, 
    FileJsonIcon, 
    FileSpreadsheetIcon, 
    FileArchiveIcon 
} from 'lucide-react';
import { Badge } from "../../ui/badge";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Message interface for the FileContentsToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// File contents response structure
interface FileContentsResponse {
    type: string;
    encoding: string;
    size: number;
    name: string;
    path: string;
    content: string;
    sha: string;
    url: string;
    git_url: string;
    html_url: string;
    download_url: string;
}

// Function to extract file extension
const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// Function to map file extension to language for syntax highlighting
const getLanguageForExtension = (ext: string): string => {
    const languageMap: Record<string, string> = {
        // Common languages
        'js': 'javascript',
        'jsx': 'jsx',
        'ts': 'typescript',
        'tsx': 'tsx',
        'py': 'python',
        'java': 'java',
        'c': 'c',
        'cpp': 'cpp',
        'cs': 'csharp',
        'go': 'go',
        'rb': 'ruby',
        'rs': 'rust',
        'php': 'php',
        'swift': 'swift',
        'kt': 'kotlin',
        'scala': 'scala',
        'html': 'html',
        'css': 'css',
        'scss': 'scss',
        'less': 'less',
        'sh': 'bash',
        'bash': 'bash',
        'md': 'markdown',
        'json': 'json',
        'yaml': 'yaml',
        'yml': 'yaml',
        'xml': 'xml',
        'sql': 'sql',
        'graphql': 'graphql',
        'dockerfile': 'dockerfile',
    };
    
    return languageMap[ext] || 'text';
};

// Function to format file size
const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) {
        return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
        return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
        return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
};

// Function to extract repository name from GitHub URL
const extractRepoInfo = (htmlUrl: string): { repoName: string, repoUrl: string } => {
    try {
        // Extract repo name from URL pattern like https://github.com/owner/repo/blob/...
        const urlParts = htmlUrl.split('/');
        const ownerIndex = urlParts.indexOf('github.com') + 1;
        
        if (ownerIndex > 0 && ownerIndex + 1 < urlParts.length) {
            const owner = urlParts[ownerIndex];
            const repo = urlParts[ownerIndex + 1];
            const repoName = `${owner}/${repo}`;
            const repoUrl = `https://github.com/${repoName}`;
            return { repoName, repoUrl };
        }
    } catch (error) {
        console.error('Error extracting repo info:', error);
    }
    
    return { repoName: '', repoUrl: '' };
};

// Function to get appropriate icon based on file extension
const getFileIcon = (filename: string) => {
    const extension = getFileExtension(filename);
    
    // Code files
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'cs', 'go', 'rb', 'rs', 'php', 'swift'].includes(extension)) {
        return <FileCodeIcon className="h-4 w-4 text-green-400" />;
    }
    
    // Data files
    if (['json', 'yaml', 'yml', 'xml', 'csv', 'toml'].includes(extension)) {
        return <FileJsonIcon className="h-4 w-4 text-blue-400" />;
    }
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(extension)) {
        return <ImageIcon className="h-4 w-4 text-purple-400" />;
    }
    
    // Spreadsheet files
    if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) {
        return <FileSpreadsheetIcon className="h-4 w-4 text-green-500" />;
    }
    
    // Archive files
    if (['zip', 'tar', 'gz', 'rar', '7z'].includes(extension)) {
        return <FileArchiveIcon className="h-4 w-4 text-yellow-500" />;
    }
    
    // Text files
    if (['txt', 'md', 'rtf', 'html', 'css'].includes(extension)) {
        return <FileTextIcon className="h-4 w-4 text-gray-400" />;
    }
    
    // Default file icon
    return <FileIcon className="h-4 w-4" />;
};

// Function to render a single file content view
const renderFileContents = (fileContents: FileContentsResponse) => {
    // Decode base64 content if needed
    let decodedContent = fileContents.content;
    if (fileContents.encoding === 'base64') {
        try {
            decodedContent = atob(fileContents.content);
        } catch (error) {
            console.error('Error decoding base64 content:', error);
        }
    }

    // Get the language for syntax highlighting
    const fileExtension = getFileExtension(fileContents.name);
    const language = getLanguageForExtension(fileExtension);
    
    // Extract repository information
    const { repoName, repoUrl } = extractRepoInfo(fileContents.html_url);

    return (
        <div className="border border-[#1D2739] rounded-lg overflow-hidden bg-[#131B2E]/30 mt-2">
            {/* File header */}
            <div className="flex items-center justify-between gap-2 p-2 border-b border-[#1D2739] bg-backgrounddark">
                <div className="flex items-center gap-2">
                    {getFileIcon(fileContents.name)}
                    {repoName && (
                        <span className="font-medium">
                            <a 
                                href={repoUrl}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300"
                            >
                                {repoName}
                            </a>
                            <span className="text-textmedium mx-1">/</span>
                        </span>
                    )}
                    <span className="font-medium">{fileContents.path}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20">
                        {formatFileSize(fileContents.size)}
                    </Badge>
                    <a 
                        href={fileContents.html_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-textmedium hover:text-textlight"
                    >
                        View on GitHub
                        <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            </div>
            
            {/* Code content with syntax highlighting */}
            <div className="max-h-[500px] overflow-auto">
                <SyntaxHighlighter
                    language={language}
                    style={vscDarkPlus}
                    showLineNumbers={true}
                    customStyle={{ 
                        margin: 0, 
                        borderRadius: 0,
                        background: 'transparent'
                    }}
                >
                    {decodedContent}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};

// Function to render a directory listing view
const renderDirectoryListing = (files: FileContentsResponse[]) => {
    // Get the common directory path from the first file
    const directoryPath = files.length > 0 ? files[0].path.split('/').slice(0, -1).join('/') : '';
    
    // Extract repository information from the first file
    const { repoName, repoUrl } = files.length > 0 
        ? extractRepoInfo(files[0].html_url) 
        : { repoName: '', repoUrl: '' };

    return (
        <div className="border border-[#1D2739] rounded-lg overflow-hidden bg-[#131B2E]/30 mt-2">
            {/* Directory header */}
            <div className="flex items-center justify-between gap-2 p-2 border-b border-[#1D2739] bg-backgrounddark">
                <div className="flex items-center gap-2">
                    <FolderIcon className="h-4 w-4" />
                    {repoName && (
                        <span className="font-medium">
                            <a 
                                href={repoUrl}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300"
                            >
                                {repoName}
                            </a>
                            <span className="text-textmedium mx-1">/</span>
                        </span>
                    )}
                    <span className="font-medium">{directoryPath}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20">
                        {files.length} items
                    </Badge>
                </div>
            </div>
            
            {/* File list */}
            <div className="max-h-[500px] overflow-auto divide-y divide-[#1D2739]">
                {files.map((file, index) => (
                    <div 
                        key={index} 
                        className="flex items-center justify-between p-2 hover:bg-[#1D2739]/20"
                    >
                        <div className="flex items-center gap-2">
                            {file.type === 'dir' ? (
                                <FolderIcon className="h-4 w-4 text-yellow-400" />
                            ) : (
                                getFileIcon(file.name)
                            )}
                            <span>{file.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-xs text-textmedium">
                                {formatFileSize(file.size)}
                            </span>
                            <a 
                                href={file.html_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-textmedium hover:text-textlight"
                            >
                                View
                                <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Tool renderer for file contents
export function FileContentsToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let parsedResponse: FileContentsResponse | FileContentsResponse[] | null = null;
    try {
        parsedResponse = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing file contents response data:', error);
        return (
            <div className="w-full">
                <MarkdownRenderer content={message.content} />
                <div className="border border-[#1D2739] rounded-lg p-4 bg-red-500/10 text-red-400 mt-2">
                    Error: Unable to parse the file contents response. Invalid JSON format.
                </div>
            </div>
        );
    }

    // Validate the response is not null
    if (!parsedResponse) {
        return (
            <div className="w-full">
                <MarkdownRenderer content={message.content} />
                <div className="border border-[#1D2739] rounded-lg p-4 bg-red-500/10 text-red-400 mt-2">
                    Error: Received empty response data.
                </div>
            </div>
        );
    }

    // Determine if we have a valid file or directory listing
    const isValidFile = !Array.isArray(parsedResponse) && 
                        parsedResponse.content !== undefined && 
                        parsedResponse.path !== undefined;
    
    const isValidDirListing = Array.isArray(parsedResponse) && 
                              parsedResponse.length > 0 && 
                              parsedResponse.every(item => 
                                item.type !== undefined && 
                                item.name !== undefined && 
                                item.path !== undefined
                              );

    // Handle invalid formats
    if (!isValidFile && !isValidDirListing) {
        return (
            <div className="w-full">
                <MarkdownRenderer content={message.content} />
                <div className="border border-[#1D2739] rounded-lg p-4 bg-red-500/10 text-red-400 mt-2">
                    Error: Response format is not recognized as either a file or directory listing.
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <MarkdownRenderer content={message.content} />
            
            {/* Render either a single file or a directory listing based on the response type */}
            {Array.isArray(parsedResponse) 
                ? renderDirectoryListing(parsedResponse) 
                : (parsedResponse.content ? renderFileContents(parsedResponse) : null)
            }
        </div>
    );
}

// Static method to determine if this renderer should render a message
FileContentsToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};